/**
 * Application-wide logger with level-based filtering.
 *
 * In production builds (import.meta.env.PROD === true), only errors
 * are logged by default. In development, all levels are enabled.
 */

/* eslint-disable no-console */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'none';

const LEVEL_ORDER: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  none: 4,
};

class Logger {
  private level: LogLevel;

  constructor() {
    // Default to 'error' in production, 'debug' in development
    try {
      this.level = import.meta.env.PROD ? 'error' : 'debug';
    } catch {
      // Fallback for environments where import.meta.env is not available (e.g. tests)
      this.level = 'debug';
    }
  }

  setLevel(level: LogLevel): void {
    this.level = level;
  }

  getLevel(): LogLevel {
    return this.level;
  }

  debug(message: string, ...args: unknown[]): void {
    if (LEVEL_ORDER[this.level] <= LEVEL_ORDER.debug) {
      console.log(message, ...args);
    }
  }

  info(message: string, ...args: unknown[]): void {
    if (LEVEL_ORDER[this.level] <= LEVEL_ORDER.info) {
      console.log(message, ...args);
    }
  }

  warn(message: string, ...args: unknown[]): void {
    if (LEVEL_ORDER[this.level] <= LEVEL_ORDER.warn) {
      console.warn(message, ...args);
    }
  }

  error(message: string, ...args: unknown[]): void {
    if (LEVEL_ORDER[this.level] <= LEVEL_ORDER.error) {
      console.error(message, ...args);
    }
  }
}

export const logger = new Logger();
export default Logger;
